import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EmployeeAvatar } from '@/components/Shared/EmployeeAvatar';
import { fmtHours, fmtRange, ghostOf } from '../assistantFormat';
import type { AssistantEmployee, AssistantObject, AssistantTask, SuggestionFit } from '../assistantTypes';
import { DayLine } from './DayLine';
import { ReasonChips } from './ReasonChips';
import { ScorePill } from './ScorePill';

interface Props {
  fit: SuggestionFit;
  task: AssistantTask;
  employee: AssistantEmployee;
  rank: number;
  objects: Map<string, AssistantObject>;
  active: boolean;
  disabled?: boolean;
  newlyAssignedTaskIds: string[];
  onHover: (employeeId: string | null) => void;
  onAssign: (fit: SuggestionFit) => void;
}

/** Top candidate in the task mode: employee, score, reasons, mini day line and assign button. */
export function CandidateCard({
  fit,
  task,
  employee,
  rank,
  objects,
  active,
  disabled,
  newlyAssignedTaskIds,
  onHover,
  onAssign,
}: Props) {
  const { t, i18n } = useTranslation('planning');
  const free = t('assistant.hoursFree', { hours: fmtHours(employee.freeMinutes, i18n.language) });
  return (
    <div
      onMouseEnter={() => onHover(employee.employeeId ?? null)}
      onMouseLeave={() => onHover(null)}
      className={`bg-white rounded-xl border p-3.5 flex flex-col gap-2.5 min-w-0 ${
        active ? 'border-slate-400' : 'border-slate-200'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <EmployeeAvatar employee={{ name: employee.name, avatarColor: employee.avatarColor }} size={32} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-slate-900 truncate">{employee.name}</div>
          <div className="text-[11px] text-slate-500 tabular-nums">
            {rank === 0 ? `${t('assistant.taskMode.bestChoice')} · ${free}` : free}
          </div>
        </div>
        <ScorePill score={fit.score} />
      </div>
      <ReasonChips reasons={fit.reasons} objects={objects} startSource={employee.startPoint?.source} />
      <DayLine
        employee={employee}
        ghost={ghostOf(fit, task)}
        size="compact"
        newlyAssignedTaskIds={newlyAssignedTaskIds}
      />
      <button
        type="button"
        onClick={() => onAssign(fit)}
        disabled={disabled}
        className={`whitespace-nowrap inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
          rank === 0
            ? 'bg-[#00A871] text-white hover:bg-emerald-700'
            : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
        }`}
      >
        <Plus size={13} strokeWidth={2} />
        {t('assistant.assign')}
        <span className="tabular-nums">{fmtRange(fit.start, fit.end)}</span>
      </button>
    </div>
  );
}
